import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  MDBRow,
  MDBCol,
  MDBCardBody,
  MDBBadge,
  MDBRadio,
  MDBCard,
  MDBSpinner,
} from "mdb-react-ui-kit";

function CommitteeMembers({ members }) {
  const [legislators, setLegislators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partyFilter, setPartyFilter] = useState("all");

  useEffect(() => {
    const fetchLegislators = async () => {
      const legislatorIds = (members || []).map((member) => member.legislatorId);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_LAWS_BACKEND_HOST}/legislators/v1/legislators/search?limit=100&offset=0`,
          { legislatorIds }
        );
        const fetchedLegislators = response.data.content || [];

        const emailAddresses = fetchedLegislators.map(
          (legislator) => legislator.emailAddress
        );
        if (emailAddresses.length > 0) {
          const wpprofileResponse = await axios.post(
            `${process.env.REACT_APP_WP_HOST}/wp-json/legmt/v1/legislators/findByEmails`,
            { emails: emailAddresses }
          );

          const wpprofileData = wpprofileResponse.data;

          const legislatorsWithWPDate = fetchedLegislators.map((legislator) => {
            const wpprofile = wpprofileData.find(
              (profile) => profile.emailAddress === legislator.emailAddress
            );
            return {
              ...legislator,
              profilePictureUrl: wpprofile?.photo?.url || null,
              profileUrl: wpprofile?.links?.profileUrl || null,
            };
          });

          setLegislators(legislatorsWithWPDate);
        }
      } catch (error) {
        console.error("Error fetching legislator details or WP Data:", error);
      } finally {
        setLoading(false); // Stop loading when data is fetched
      }
    };

    fetchLegislators();
  }, [members]);

  const combinedMembers =  (members || []).map((member) => {
    const legislator = legislators.find((l) => l.id === member.legislatorId);
    return {
      ...member,
      ...legislator,
    };
  });

  const filteredMembers = (combinedMembers || []).filter((member) => {
    if (partyFilter === "all") {
      return (
        member?.politicalParty?.code === "R" ||
        member?.politicalParty?.code === "D"
      );
    }
    return member?.politicalParty?.code === partyFilter;
  });

  return (
    <>
      <MDBRow className="mb-3">
        <MDBCol>
          <h5 className="sub-heading">Members</h5>
        </MDBCol>
        <MDBCol size="auto">
          <div className="d-flex justify-content-end badge-filter">
            <MDBRadio
              name="inlineRadio"
              value="all"
              label={<span className="badge">All</span>}
              inline
              defaultChecked
              onChange={() => setPartyFilter("all")}
            />
            <MDBRadio
              name="inlineRadio"
              value="D"
              label={<span className="badge bg-custom7">Democrat (D)</span>}
              inline
              onChange={() => setPartyFilter("D")}
            />
            <MDBRadio
              name="inlineRadio"
              value="R"
              label={<span className="badge bg-custom6">Republican (R)</span>}
              inline
              onChange={() => setPartyFilter("R")}
            />
          </div>
        </MDBCol>
      </MDBRow>
      <MDBRow id="members" className="g-2">
        {loading ? (
          // Show spinner while loading
          <div className="d-flex justify-content-center mt-5">
            <MDBSpinner role="status">
              <span className="visually-hidden">Loading...</span>
            </MDBSpinner>
          </div>
        ) : filteredMembers.length > 0 ? (
          filteredMembers
            .sort((a, b) => {
              const typeOrder = { Chair: 0, "Vice Chair": 1, Member: 2 };
              const typeA = typeOrder[a?.type?.name] ?? 3; // Default to 3 for unknown types
              const typeB = typeOrder[b?.type?.name] ?? 3;

              // First sort by type order
              if (typeA !== typeB) {
                return typeA - typeB;
              }

              // Then sort alphabetically by last name
              return a.lastName.localeCompare(b.lastName, undefined, {
                sensitivity: "base",
              });
            })
            .map((member, index, sortedArray) => {
              const isHrNeeded =
                index > 0 &&
                sortedArray[index - 1]?.type?.name !== "Member" &&
                member?.type?.name === "Member";

              const imageUrl = member.profilePictureUrl
                ? member.profilePictureUrl
                : null;
              const profileUrl = member.profileUrl || "#";

              return (
                <React.Fragment key={index}>
                  {isHrNeeded && <hr className="mt-4" />}
                  <MDBCol xl={4} md={6}>
                    <a
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MDBCard className="profile rounded-0 border-0 bg-transparent shadow-none">
                        <MDBCardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <div className="portrait rounded-circle d-flex align-items-center justify-content-center text-white text-center">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={
                                      member.firstName +
                                      " " +
                                      member.lastName +
                                      " Portrait"
                                    }
                                    className="d-block"
                                  />
                                ) : (
                                  <div>
                                    No <br /> Photo
                                  </div>
                                )}
                              </div>
                              <div className="ms-3">
                                <h6 className="sub-heading text-muted m-0 d-flex align-items-center">
                                  {member.chamber === "SENATE"
                                    ? "Senator"
                                    : member.chamber === "HOUSE"
                                    ? "Representative"
                                    : " "}
                                  <MDBBadge className="ms-2"
                                    pill
                                    color={
                                      member?.politicalParty?.code === "R"
                                        ? "custom6"
                                        : "custom7"
                                    }
                                  >
                                    ({member?.politicalParty?.code || "N/A"})
                                  </MDBBadge>
                                </h6>
                                <h5 className="mb-0">
                                  {member.lastName}, {member.firstName}
                                </h5>
                                <h6 className="text-body member-type mb-0">
                                  {member?.type?.name || "No Type"}
                                </h6>
                              </div>
                            </div>
                          </div>
                        </MDBCardBody>
                      </MDBCard>
                    </a>
                  </MDBCol>
                </React.Fragment>
              );
            })
        ) : (
          <p className="small text-center mt-5">
            No members found for this party.
          </p>
        )}
      </MDBRow>
    </>
  );
}

export default CommitteeMembers;
